import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Typography, Dialog } from '@mui/material'
import colors from 'styles/colors'
import RowStack from 'components/atoms/container/RowStack'
import { ColStack } from 'components/atoms/container/InCardStack'
import {
  PopupSubHeader,
  MilestoneBox,
  CardTag,
  CardSkeleton
} from 'components/molecules/home/PathCardMolecules'
import PathCardActions from 'components/molecules/home/PathCardActions'
import { Close } from '@mui/icons-material'
import { FlatIconButton } from 'components/atoms/button/StyledButton'
import { getGoalSteps } from 'utils/getSupportingInfo'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { compileGoalSteps } from 'services/goalServices'

const PathDetailsPopup = ({
  open,
  onClose,
  goal,
  isLibrary,
  forOnboarding = false,
  onboardingCallback = () => {}
}) => {
  const l1Milestones = goal?.l1Steps?.slice(0, -1) || [] // remove the last milestone (Completion)
  const skills = goal?.virtues || []
  const [loading, setLoading] = useState(false)
  const [goalLengthSeconds, setGoalLengthSeconds] = useState(0)
  const [goalRemainingSeconds, setGoalRemainingSeconds] = useState(0)
  const goalProgress = goal?.progress

  useEffect(() => {
    // Fetch goal steps if not already fetched (Incase of library goals)
    const fetchSteps = async () => {
      setLoading(true)
      const steps = await getGoalSteps(goal.id)
      const { stats } = compileGoalSteps(
        steps,
        goal?.progress?.currentGoalStepId
      )
      setGoalLengthSeconds(stats?.goalLengthSeconds)
      setGoalRemainingSeconds(stats?.remainingSeconds)
      setLoading(false)
    }
    if (goal?.stats?.goalLengthSeconds) {
      setGoalLengthSeconds(goal.stats.goalLengthSeconds)
      setGoalRemainingSeconds(goal.stats.remainingSeconds)
    } else {
      fetchSteps()
    }
  }, [goal])

  return (
    <>
      <OverlayLoading open={loading} />
      <Dialog onClose={onClose} open={open} maxWidth={'sm'}>
        <FlatIconButton
          icon={<Close />}
          iconContainerProps={{ size: 24 }}
          onClick={onClose}
          sx={{ position: 'absolute', right: 16, top: 16 }}
        />
        <ColStack
          spacing={12}
          sx={{
            width: 420,
            paddingX: 12,
            paddingY: 16,
            borderRadius: 3
          }}>
          <ColStack spacing={4}>
            <Typography variant='h2'>{goal.name}</Typography>
            <PopupSubHeader
              l1Milestones={l1Milestones}
              skills={skills}
              goalLengthSeconds={goalLengthSeconds}
              goalRemainingSeconds={goalRemainingSeconds}
              isActive={goal?.active}
              showEstTime={forOnboarding}
            />
          </ColStack>

          <ColStack spacing={4}>
            {l1Milestones.length > 0 ? (
              <>
                {l1Milestones.map(milestone => (
                  <MilestoneBox
                    key={milestone.id}
                    milestone={milestone}
                    isCompleted={
                      milestone.number <= goalProgress?.completedL1StepNumber
                    }
                  />
                ))}
              </>
            ) : (
              <>
                {Array.from({ length: 5 }).map((_, index) => (
                  <CardSkeleton key={uuidv4()} w={372} h={40} />
                ))}
              </>
            )}
          </ColStack>

          <RowStack spacing={0} gap={4} flexWrap={'wrap'}>
            {skills.length > 0 ? (
              <>
                {skills.map(skill => (
                  <CardTag
                    key={skill.id}
                    sx={{ backgroundColor: colors.Grey }}
                    showEllipsis={false}>
                    {skill.name}
                  </CardTag>
                ))}
              </>
            ) : (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <CardSkeleton key={uuidv4()} w={55} h={16} />
                ))}
              </>
            )}
          </RowStack>

          <PathCardActions
            goal={goal}
            isLibrary={isLibrary}
            forOnboarding={forOnboarding}
            onboardingCallback={onboardingCallback}
            containerProps={{ justifyContent: 'center' }}
            hideLeftButton={goal.opensAt}
          />
        </ColStack>
      </Dialog>
    </>
  )
}

export default PathDetailsPopup
